'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface BookingFormProps {
  selectedServices: string[];
  onSubmit: (data: FormData) => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName: string;
  tellUsMore: string;
  organizationType: string;
  organizationSize: string;
  preferredDate: string;
  preferredTime: string;
  serviceType: string;
  specificConcerns: string;
  currentChallenges: string;
  complianceIssues: string;
  budgetRange: string;
  timelineExpectation: string;
  hipaaConsent: boolean;
}

const serviceNameMap: Record<string, string> = {
  'revenue-cycle-management': 'Revenue Cycle Management',
  'medical-billing-coding': 'Medical Billing & Coding',
  'hipaa-compliance-security': 'HIPAA Compliance & Security',
  'medical-malpractice-risk': 'Medical Malpractice Risk Management',
  'practice-operations-leadership': 'Medical Practice Operations & Leadership Training',
  'medical-staffing-recruiting': 'Medical Staffing & Recruiting',
  'hcc-coding-risk-adjustment': 'HCC Coding & Risk Adjustment',
  'contracting': 'Contracting',
  'medical-staff-credentialing': 'Medical Staff Credentialing',
  'practice-valuation-sale': 'Practice Valuation & Sale Advisory',
  'occupational-health-wellness': 'Occupational Health, Wellness & Safety',
  'lab-services': 'Lab Services',
  'mergers-acquisitions': 'Mergers and Acquisitions',
  'new-site-development': 'New Site Development',
  'hyperbaric-consulting': 'Hyperbaric Consulting',
};

const PRIMARY_CONSULTATION_EMAIL_RECIPIENT =
  'messages@totalmedicalpracticesupport.com';

const buildConsultationEmailBody = (data: FormData, selectedServices: string[]) => {
  const serviceList = selectedServices.length
    ? selectedServices.map(id => serviceNameMap[id] || id).join(', ')
    : 'Not specified';

  const submittedAt = new Date().toLocaleString();
  const notes = data.tellUsMore?.trim() ? data.tellUsMore.trim() : 'No additional details provided.';
  const preferredDate = data.preferredDate || 'Not provided';
  const preferredTime = data.preferredTime || 'Not provided';

  const detailsSections = [
    'NEW CONSULTATION REQUEST',
    '',
    'CONTACT INFORMATION',
    `Name: ${`${data.firstName} ${data.lastName}`.trim() || 'Not provided'}`,
    `Email: ${data.email || 'Not provided'}`,
    `Phone: ${data.phone || 'Not provided'}`,
    `Company/Practice: ${data.companyName || 'Not provided'}`,
    '',
    'CONSULTATION DETAILS',
    `Selected Services: ${serviceList} |`,
    `Preferred Date: ${preferredDate} | Preferred Time: ${preferredTime}`,
    '',
    'ADDITIONAL NOTES',
    notes.replace(/\s+/g, ' ').trim(),
    '',
    `SUBMISSION METADATA Submitted At: ${submittedAt}`,
  ];

  return detailsSections.join('\n');
};

const BookingForm = ({ selectedServices, onSubmit }: BookingFormProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    companyName: '',
    tellUsMore: '',
    organizationType: '',
    organizationSize: '',
    preferredDate: '',
    preferredTime: '',
    serviceType: selectedServices.join(','),
    specificConcerns: '',
    currentChallenges: '',
    complianceIssues: '',
    budgetRange: '',
    timelineExpectation: '',
    hipaaConsent: false,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [currentStep, setCurrentStep] = useState(1);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (selectedServices.length > 0) {
      const serviceNames = selectedServices.map(id => serviceNameMap[id] || id).join(', ');
      const servicesText = `Selected Services: ${serviceNames}`;
      
      setFormData((prev) => {
        // Remove any existing "Selected Services:" line and add the new one
        const currentText = prev.tellUsMore || '';
        const lines = currentText.split('\n').filter(line => !line.startsWith('Selected Services:'));
        const userContent = lines.join('\n').trim();
        const newText = userContent 
          ? `${servicesText}\n\n${userContent}` 
          : servicesText;
        
        return {
          ...prev,
          tellUsMore: newText,
          serviceType: selectedServices.join(','),
        };
      });
    } else {
      // Remove the "Selected Services:" line if no services are selected
      setFormData((prev) => {
        const currentText = prev.tellUsMore || '';
        const lines = currentText.split('\n').filter(line => !line.startsWith('Selected Services:'));
        const newText = lines.join('\n').trim();
        
        return {
          ...prev,
          tellUsMore: newText,
          serviceType: '',
        };
      });
    }
  }, [selectedServices]);

  const organizationTypes = [
    'Hospital/Health System',
    'Medical Practice (Single Location)',
    'Medical Practice (Multi-Location)',
    'Specialty Clinic',
    'Urgent Care Center',
    'Telehealth Provider',
    'Healthcare Startup',
    'Other Healthcare Organization',
  ];

  const organizationSizes = [
    '1-10 employees',
    '11-50 employees',
    '51-200 employees',
    '201-500 employees',
    '500+ employees',
  ];

  const budgetRanges = [
    'Under $10,000',
    '$10,000 - $25,000',
    '$25,000 - $50,000',
    '$50,000 - $100,000',
    '$100,000+',
    'Not sure yet',
  ];

  const timelineExpectations = [
    'Immediate (Within 1 week)',
    'Urgent (1-2 weeks)',
    'Standard (2-4 weeks)',
    'Flexible (1-3 months)',
    'Long-term planning',
  ];

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (submitError) setSubmitError('');
    if (submitSuccess) setSubmitSuccess(false);

    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (step === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      // if (!formData.phone.trim()) {
      //   newErrors.phone = 'Phone number is required';
      // } else if (!/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(formData.phone)) {
      //   newErrors.phone = 'Please enter a valid phone number';
      // }
    }

    // if (step === 2) {
    //   if (!formData.organizationType) newErrors.organizationType = 'Organization type is required';
    //   if (!formData.organizationSize) newErrors.organizationSize = 'Organization size is required';
    //   if (!formData.preferredDate) newErrors.preferredDate = 'Preferred date is required';
    //   if (!formData.preferredTime) newErrors.preferredTime = 'Preferred time is required';
    // }

    // if (step === 3) {
    //   if (!formData.currentChallenges.trim()) {
    //     newErrors.currentChallenges = 'Please describe your current challenges';
    //   }
    //   if (!formData.budgetRange) newErrors.budgetRange = 'Budget range is required';
    //   if (!formData.timelineExpectation) {
    //     newErrors.timelineExpectation = 'Timeline expectation is required';
    //   }
    //   if (!formData.hipaaConsent) {
    //     newErrors.hipaaConsent = 'HIPAA consent is required to proceed';
    //   }
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      // Skip steps 2 and 3, go directly to submission
      handleSubmit({ preventDefault: () => {} } as React.FormEvent);
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Only validate step 1 since we're skipping steps 2 and 3
    if (validateStep(1)) {
      setIsSubmitting(true);
      setSubmitError('');
      setSubmitSuccess(false);

      try {
        const details = buildConsultationEmailBody(formData, selectedServices);
        const baseParams = {
          FirstName: formData.firstName || 'Not provided',
          LastName: formData.lastName || 'Not provided',
          Email: formData.email || 'Not provided',
          Phone: formData.phone || 'Not provided',
          Details: details,
        };

        const sendToRecipient = async (recipient: string) => {
          const params = new URLSearchParams({ ...baseParams, recipient });
          const response = await fetch(
            `https://api.totalmedicalpracticesupport.com/api/totalmedicalemail?${params.toString()}`,
            { method: 'GET' }
          );

          if (!response.ok) {
            throw new Error(`Email request failed (${response.status})`);
          }

          const payload = await response.json().catch(() => null);
          if (payload && payload.success === false) {
            throw new Error(payload.message || 'Email request failed');
          }
        };

        const recipients = [PRIMARY_CONSULTATION_EMAIL_RECIPIENT];
        const userEmail = formData.email.trim();
        if (userEmail) {
          recipients.push(userEmail);
        }
        await Promise.all(recipients.map(sendToRecipient));

        setSubmitSuccess(true);
        onSubmit(formData);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'We could not send your request. Please try again.';
        setSubmitError(message);
        return;
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:317:6" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="317" data-component-end-line="326" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-white%20rounded-lg%20shadow-diagnostic%20p-8%22%7D" className="bg-white rounded-lg shadow-diagnostic p-8">
        <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:318:8" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="318" data-component-end-line="325" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-6%22%7D" className="animate-pulse space-y-6">
          <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:319:10" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="319" data-component-end-line="319" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20bg-muted%20rounded%20w-3%2F4%22%7D" className="h-8 bg-muted rounded w-3/4"></div>
          <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:320:10" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="320" data-component-end-line="324" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
            <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:321:12" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="321" data-component-end-line="321" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-muted%20rounded%22%7D" className="h-12 bg-muted rounded"></div>
            <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:322:12" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="322" data-component-end-line="322" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-muted%20rounded%22%7D" className="h-12 bg-muted rounded"></div>
            <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:323:12" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="323" data-component-end-line="323" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-muted%20rounded%22%7D" className="h-12 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:331:4" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="331" data-component-end-line="767" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-white%20rounded-lg%20shadow-diagnostic%22%7D" className="bg-white rounded-lg shadow-diagnostic">
      <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:332:6" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="332" data-component-end-line="357" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-6%20border-b%20border-border%22%7D" className="p-6 border-b border-border">
        <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:333:8" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="333" data-component-end-line="346" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-6%22%7D" className="flex items-center justify-between mb-6">
          {[1].map((step) => (
            <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:335:12" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="335" data-component-end-line="344" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20flex-1%22%7D" key={step} className="flex items-center flex-1">
              <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:336:14" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="336" data-component-end-line="343" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold transition-medical ${
                  currentStep >= step
                    ? 'bg-primary text-white' :'bg-muted text-text-secondary'
                }`}
              >
                {step}
              </div>
            </div>
          ))}
        </div>
        <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:347:8" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="347" data-component-end-line="356" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%22%7D" className="text-center">
          <h3 data-component-id="src/app/consultation-booking/components/BookingForm.tsx:348:10" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="348" data-component-end-line="352" data-component-file="BookingForm.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-lg%20font-semibold%20text-text-primary%22%7D" className="text-lg font-semibold text-text-primary">
            {currentStep === 1 && 'Contact Information'}
            {/* {currentStep === 2 && 'Organization Details'} */}
            {/* {currentStep === 3 && 'Assessment & Consent'} */}
          </h3>
          <p data-component-id="src/app/consultation-booking/components/BookingForm.tsx:353:10" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="353" data-component-end-line="355" data-component-file="BookingForm.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-text-secondary%20mt-1%22%2C%22textContent%22%3A%22Step%20of%201%22%7D" className="text-sm text-text-secondary mt-1">
            Step {currentStep} of 1
          </p>
        </div>
      </div>

      <form data-component-id="src/app/consultation-booking/components/BookingForm.tsx:359:6" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="359" data-component-end-line="766" data-component-file="BookingForm.tsx" data-component-name="form" data-component-content="%7B%22elementName%22%3A%22form%22%2C%22className%22%3A%22p-8%22%7D" onSubmit={handleSubmit} className="p-8">
        {currentStep === 1 && (
          <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:361:10" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="361" data-component-end-line="502" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
            <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:362:12" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="362" data-component-end-line="402" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:363:14" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="363" data-component-end-line="381" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                <label data-component-id="src/app/consultation-booking/components/BookingForm.tsx:364:16" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="364" data-component-end-line="366" data-component-file="BookingForm.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-text-primary%20mb-2%22%2C%22textContent%22%3A%22First%20Name%22%7D" htmlFor="firstName" className="block text-sm font-medium text-text-primary mb-2">
                  First Name <span data-component-id="src/app/consultation-booking/components/BookingForm.tsx:365:29" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="365" data-component-end-line="365" data-component-file="BookingForm.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-error%22%2C%22textContent%22%3A%22*%22%7D" className="text-error">*</span>
                </label>
                <input data-component-id="src/app/consultation-booking/components/BookingForm.tsx:367:16" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="367" data-component-end-line="377" data-component-file="BookingForm.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22id%22%3A%22firstName%22%2C%22name%22%3A%22firstName%22%7D"
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary transition-medical ${
                    errors.firstName ? 'border-error' : 'border-input'
                  }`}
                  placeholder="John"
                />
                {errors.firstName && (
                  <p data-component-id="src/app/consultation-booking/components/BookingForm.tsx:379:18" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="379" data-component-end-line="379" data-component-file="BookingForm.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-error%20text-sm%20mt-1%22%7D" className="text-error text-sm mt-1">{errors.firstName}</p>
                )}
              </div>

              <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:383:14" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="383" data-component-end-line="401" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                <label data-component-id="src/app/consultation-booking/components/BookingForm.tsx:384:16" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="384" data-component-end-line="386" data-component-file="BookingForm.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-text-primary%20mb-2%22%2C%22textContent%22%3A%22Last%20Name%22%7D" htmlFor="lastName" className="block text-sm font-medium text-text-primary mb-2">
                  Last Name <span data-component-id="src/app/consultation-booking/components/BookingForm.tsx:385:28" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="385" data-component-end-line="385" data-component-file="BookingForm.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-error%22%2C%22textContent%22%3A%22*%22%7D" className="text-error">*</span>
                </label>
                <input data-component-id="src/app/consultation-booking/components/BookingForm.tsx:387:16" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="387" data-component-end-line="397" data-component-file="BookingForm.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22id%22%3A%22lastName%22%2C%22name%22%3A%22lastName%22%7D"
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary transition-medical ${
                    errors.lastName ? 'border-error' : 'border-input'
                  }`}
                  placeholder="Smith"
                />
                {errors.lastName && (
                  <p data-component-id="src/app/consultation-booking/components/BookingForm.tsx:399:18" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="399" data-component-end-line="399" data-component-file="BookingForm.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-error%20text-sm%20mt-1%22%7D" className="text-error text-sm mt-1">{errors.lastName}</p>
                )}
              </div>
            </div>

            <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:404:12" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="404" data-component-end-line="422" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <label data-component-id="src/app/consultation-booking/components/BookingForm.tsx:405:14" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="405" data-component-end-line="407" data-component-file="BookingForm.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-text-primary%20mb-2%22%2C%22textContent%22%3A%22Email%20Address%22%7D" htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
                Email Address <span data-component-id="src/app/consultation-booking/components/BookingForm.tsx:406:30" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="406" data-component-end-line="406" data-component-file="BookingForm.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-error%22%2C%22textContent%22%3A%22*%22%7D" className="text-error">*</span>
              </label>
              <input data-component-id="src/app/consultation-booking/components/BookingForm.tsx:408:14" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="408" data-component-end-line="418" data-component-file="BookingForm.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22email%22%2C%22id%22%3A%22email%22%2C%22name%22%3A%22email%22%7D"
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary transition-medical ${
                  errors.email ? 'border-error' : 'border-input'
                }`}
                placeholder="john.smith@hospital.com"
              />
              {errors.email && (
                <p data-component-id="src/app/consultation-booking/components/BookingForm.tsx:420:16" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="420" data-component-end-line="420" data-component-file="BookingForm.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-error%20text-sm%20mt-1%22%7D" className="text-error text-sm mt-1">{errors.email}</p>
              )}
            </div>

            {/* <div>
              <label htmlFor="phone" className="block text-sm font-medium text-text-primary mb-2">
                Phone Number <span className="text-error">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary transition-medical ${
                  errors.phone ? 'border-error' : 'border-input'
                }`}
                placeholder="(555) 123-4567"
              />
              {errors.phone && (
                <p className="text-error text-sm mt-1">{errors.phone}</p>
              )}
            </div> */}

            <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:444:12" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="444" data-component-end-line="457" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <label data-component-id="src/app/consultation-booking/components/BookingForm.tsx:445:14" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="445" data-component-end-line="447" data-component-file="BookingForm.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-text-primary%20mb-2%22%2C%22textContent%22%3A%22Company%2FPractice%20Name%22%7D" htmlFor="companyName" className="block text-sm font-medium text-text-primary mb-2">
                Company/Practice Name <span data-component-id="src/app/consultation-booking/components/BookingForm.tsx:446:38" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="446" data-component-end-line="446" data-component-file="BookingForm.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-text-secondary%20text-xs%20font-normal%22%2C%22textContent%22%3A%22(Optional)%22%7D" className="text-text-secondary text-xs font-normal">(Optional)</span>
              </label>
              <input data-component-id="src/app/consultation-booking/components/BookingForm.tsx:448:14" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="448" data-component-end-line="456" data-component-file="BookingForm.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22id%22%3A%22companyName%22%2C%22name%22%3A%22companyName%22%2C%22className%22%3A%22w-full%20px-4%20py-3%20border%20border-input%20rounded-md%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-primary%20transition-medical%22%7D"
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary transition-medical"
                placeholder="ABC Medical Group"
              />
            </div>

            <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:459:12" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="459" data-component-end-line="489" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:460:14" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="460" data-component-end-line="479" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mb-2%22%7D" className="flex items-center justify-between mb-2">
                <label data-component-id="src/app/consultation-booking/components/BookingForm.tsx:461:16" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="461" data-component-end-line="463" data-component-file="BookingForm.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-text-primary%22%2C%22textContent%22%3A%22Tell%20Us%20More%22%7D" htmlFor="tellUsMore" className="block text-sm font-medium text-text-primary">
                  Tell Us More
                </label>
                {formData.tellUsMore && (
                  <button data-component-id="src/app/consultation-booking/components/BookingForm.tsx:465:18" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="465" data-component-end-line="477" data-component-file="BookingForm.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22text-xs%20text-text-secondary%20hover%3Atext-text-primary%20transition-medical%20flex%20items-center%20space-x-1%22%7D"
                    type="button"
                    onClick={() => {
                      setFormData((prev) => ({
                        ...prev,
                        tellUsMore: '',
                      }));
                    }}
                    className="text-xs text-text-secondary hover:text-text-primary transition-medical flex items-center space-x-1"
                  >
                    <Icon data-component-id="src/app/consultation-booking/components/BookingForm.tsx:475:20" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="475" data-component-end-line="475" data-component-file="BookingForm.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22XMarkIcon%22%7D" name="XMarkIcon" size={14} />
                    <span data-component-id="src/app/consultation-booking/components/BookingForm.tsx:476:20" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="476" data-component-end-line="476" data-component-file="BookingForm.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Clear%22%7D">Clear</span>
                  </button>
                )}
              </div>
              <textarea data-component-id="src/app/consultation-booking/components/BookingForm.tsx:480:14" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="480" data-component-end-line="488" data-component-file="BookingForm.tsx" data-component-name="textarea" data-component-content="%7B%22elementName%22%3A%22textarea%22%2C%22id%22%3A%22tellUsMore%22%2C%22name%22%3A%22tellUsMore%22%2C%22className%22%3A%22w-full%20px-4%20py-3%20border%20border-input%20rounded-md%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-primary%20transition-medical%20resize-none%22%7D"
                id="tellUsMore"
                name="tellUsMore"
                value={formData.tellUsMore}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-4 py-3 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary transition-medical resize-none"
                placeholder="Share any additional information that would help us better understand your needs..."
              ></textarea>
            </div>

            <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:491:12" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="491" data-component-end-line="501" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-brand-success%2F10%20border%20border-brand-success%2F20%20rounded-md%20p-4%22%7D" className="bg-brand-success/10 border border-brand-success/20 rounded-md p-4">
              <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:492:14" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="492" data-component-end-line="500" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20space-x-3%22%7D" className="flex items-start space-x-3">
                <Icon data-component-id="src/app/consultation-booking/components/BookingForm.tsx:493:16" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="493" data-component-end-line="493" data-component-file="BookingForm.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ShieldCheckIcon%22%2C%22className%22%3A%22text-brand-success%20flex-shrink-0%20mt-0.5%22%7D" name="ShieldCheckIcon" size={20} className="text-brand-success flex-shrink-0 mt-0.5" variant="solid" />
                <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:494:16" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="494" data-component-end-line="499" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                  <p data-component-id="src/app/consultation-booking/components/BookingForm.tsx:495:18" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="495" data-component-end-line="495" data-component-file="BookingForm.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20font-medium%20text-brand-success%22%2C%22textContent%22%3A%22HIPAA%20Compliant%22%7D" className="text-sm font-medium text-brand-success">HIPAA Compliant</p>
                  <p data-component-id="src/app/consultation-booking/components/BookingForm.tsx:496:18" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="496" data-component-end-line="498" data-component-file="BookingForm.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-text-secondary%20mt-1%22%2C%22textContent%22%3A%22All%20information%20is%20encrypted%20and%20handled%20according%20to%20HIPAA%20regulations%22%7D" className="text-xs text-text-secondary mt-1">
                    All information is encrypted and handled according to HIPAA regulations
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* {currentStep === 2 && (
          <div className="space-y-6">
            <div>
              <label htmlFor="organizationType" className="block text-sm font-medium text-text-primary mb-2">
                Organization Type <span className="text-error">*</span>
              </label>
              <select
                id="organizationType"
                name="organizationType"
                value={formData.organizationType}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary transition-medical ${
                  errors.organizationType ? 'border-error' : 'border-input'
                }`}
              >
                <option value="">Select organization type</option>
                {organizationTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              {errors.organizationType && (
                <p className="text-error text-sm mt-1">{errors.organizationType}</p>
              )}
            </div>

            <div>
              <label htmlFor="organizationSize" className="block text-sm font-medium text-text-primary mb-2">
                Organization Size <span className="text-error">*</span>
              </label>
              <select
                id="organizationSize"
                name="organizationSize"
                value={formData.organizationSize}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary transition-medical ${
                  errors.organizationSize ? 'border-error' : 'border-input'
                }`}
              >
                <option value="">Select organization size</option>
                {organizationSizes.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              {errors.organizationSize && (
                <p className="text-error text-sm mt-1">{errors.organizationSize}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="preferredDate" className="block text-sm font-medium text-text-primary mb-2">
                  Preferred Date <span className="text-error">*</span>
                </label>
                <input
                  type="date"
                  id="preferredDate"
                  name="preferredDate"
                  value={formData.preferredDate}
                  onChange={handleInputChange}
                  min={new Date().toISOString().split('T')[0]}
                  className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary transition-medical ${
                    errors.preferredDate ? 'border-error' : 'border-input'
                  }`}
                />
                {errors.preferredDate && (
                  <p className="text-error text-sm mt-1">{errors.preferredDate}</p>
                )}
              </div>

              <div>
                <label htmlFor="preferredTime" className="block text-sm font-medium text-text-primary mb-2">
                  Preferred Time <span className="text-error">*</span>
                </label>
                <select
                  id="preferredTime"
                  name="preferredTime"
                  value={formData.preferredTime}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary transition-medical ${
                    errors.preferredTime ? 'border-error' : 'border-input'
                  }`}
                >
                  <option value="">Select time</option>
                  <option value="09:00">9:00 AM</option>
                  <option value="10:00">10:00 AM</option>
                  <option value="11:00">11:00 AM</option>
                  <option value="13:00">1:00 PM</option>
                  <option value="14:00">2:00 PM</option>
                  <option value="15:00">3:00 PM</option>
                  <option value="16:00">4:00 PM</option>
                </select>
                {errors.preferredTime && (
                  <p className="text-error text-sm mt-1">{errors.preferredTime}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="specificConcerns" className="block text-sm font-medium text-text-primary mb-2">
                Specific Concerns (Optional)
              </label>
              <textarea
                id="specificConcerns"
                name="specificConcerns"
                value={formData.specificConcerns}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-3 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary transition-medical resize-none"
                placeholder="Brief description of immediate concerns..."
              ></textarea>
            </div>
          </div>
        )} */}

        {/* {currentStep === 3 && (
          <div className="space-y-6">
            <div>
              <label htmlFor="currentChallenges" className="block text-sm font-medium text-text-primary mb-2">
                Current Challenges <span className="text-error">*</span>
              </label>
              <textarea
                id="currentChallenges"
                name="currentChallenges"
                value={formData.currentChallenges}
                onChange={handleInputChange}
                rows={4}
                className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary transition-medical resize-none ${
                  errors.currentChallenges ? 'border-error' : 'border-input'
                }`}
                placeholder="Describe the main operational challenges your organization is facing..."
              ></textarea>
              {errors.currentChallenges && (
                <p className="text-error text-sm mt-1">{errors.currentChallenges}</p>
              )}
            </div>

            <div>
              <label htmlFor="complianceIssues" className="block text-sm font-medium text-text-primary mb-2">
                Compliance Issues (Optional)
              </label>
              <textarea
                id="complianceIssues"
                name="complianceIssues"
                value={formData.complianceIssues}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-3 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary transition-medical resize-none"
                placeholder="Any specific compliance concerns or recent audit findings..."
              ></textarea>
            </div>

            <div>
              <label htmlFor="budgetRange" className="block text-sm font-medium text-text-primary mb-2">
                Budget Range <span className="text-error">*</span>
              </label>
              <select
                id="budgetRange"
                name="budgetRange"
                value={formData.budgetRange}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary transition-medical ${
                  errors.budgetRange ? 'border-error' : 'border-input'
                }`}
              >
                <option value="">Select budget range</option>
                {budgetRanges.map((range) => (
                  <option key={range} value={range}>
                    {range}
                  </option>
                ))}
              </select>
              {errors.budgetRange && (
                <p className="text-error text-sm mt-1">{errors.budgetRange}</p>
              )}
            </div>

            <div>
              <label htmlFor="timelineExpectation" className="block text-sm font-medium text-text-primary mb-2">
                Timeline Expectation <span className="text-error">*</span>
              </label>
              <select
                id="timelineExpectation"
                name="timelineExpectation"
                value={formData.timelineExpectation}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary transition-medical ${
                  errors.timelineExpectation ? 'border-error' : 'border-input'
                }`}
              >
                <option value="">Select timeline</option>
                {timelineExpectations.map((timeline) => (
                  <option key={timeline} value={timeline}>
                    {timeline}
                  </option>
                ))}
              </select>
              {errors.timelineExpectation && (
                <p className="text-error text-sm mt-1">{errors.timelineExpectation}</p>
              )}
            </div>

            <div className="bg-muted rounded-md p-6 space-y-4">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="hipaaConsent"
                  name="hipaaConsent"
                  checked={formData.hipaaConsent}
                  onChange={handleInputChange}
                  className="mt-1"
                />
                <label htmlFor="hipaaConsent" className="text-sm text-text-primary">
                  <span className="font-medium">HIPAA Consent <span className="text-error">*</span></span>
                  <p className="mt-2 text-text-secondary">
                    I authorize Total Medical Practice Support to collect, use, and disclose my protected health information as necessary to provide consulting services. I understand that this information will be handled in accordance with HIPAA regulations and Total Medical Practice Support&apos;s privacy policy.
                  </p>
                </label>
              </div>
              {errors.hipaaConsent && (
                <p className="text-error text-sm">{errors.hipaaConsent}</p>
              )}
            </div>

            <div className="bg-brand-trust/10 border border-brand-trust/20 rounded-md p-4">
              <div className="flex items-start space-x-3">
                <Icon name="LockClosedIcon" size={20} className="text-brand-trust flex-shrink-0 mt-0.5" variant="solid" />
                <div>
                  <p className="text-sm font-medium text-brand-trust">Secure & Confidential</p>
                  <p className="text-xs text-text-secondary mt-1">
                    Your information is encrypted end-to-end and will never be shared with third parties
                  </p>
                </div>
              </div>
            </div>
          </div>
        )} */}

        <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:746:8" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="746" data-component-end-line="760" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20mt-8%20pt-6%20border-t%20border-border%22%7D" className="flex items-center justify-between mt-8 pt-6 border-t border-border">
          <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:747:10" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="747" data-component-end-line="747" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"></div>

          {currentStep === 1 ? (
            <button data-component-id="src/app/consultation-booking/components/BookingForm.tsx:750:12" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="750" data-component-end-line="758" data-component-file="BookingForm.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%20px-8%20py-3%20bg-brand-success%20text-white%20rounded-md%20hover%3Abg-brand-success%2F90%20transition-medical%20ml-auto%20font-semibold%20disabled%3Aopacity-60%20disabled%3Acursor-not-allowed%22%7D"
              type="button"
              onClick={handleNext}
              className="flex items-center space-x-2 px-8 py-3 bg-brand-success text-white rounded-md hover:bg-brand-success/90 transition-medical ml-auto font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              <Icon data-component-id="src/app/consultation-booking/components/BookingForm.tsx:756:14" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="756" data-component-end-line="756" data-component-file="BookingForm.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckCircleIcon%22%7D" name="CheckCircleIcon" size={20} variant="solid" />
              <span data-component-id="src/app/consultation-booking/components/BookingForm.tsx:757:14" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="757" data-component-end-line="757" data-component-file="BookingForm.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{isSubmitting ? 'Sending Request...' : 'Submit Consultation Request'}</span>
            </button>
          ) : null}
        </div>
        {(submitError || submitSuccess) && (
          <div data-component-id="src/app/consultation-booking/components/BookingForm.tsx:762:10" data-component-path="src/app/consultation-booking/components/BookingForm.tsx" data-component-line="762" data-component-end-line="764" data-component-file="BookingForm.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`mt-4 text-sm ${submitError ? 'text-error' : 'text-brand-success'}`}>
            {submitError || 'Your consultation request has been sent successfully.'}
          </div>
        )}
      </form>
    </div>
  );
};

export default BookingForm;